import { computed, watch } from "vue";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useSubscriptionStore } from "@/stores/subscription-store";

/**
 * Factory composable that gates a named feature via the subscription store.
 *
 * Watches the current Organization identity and invalidates + re-fetches
 * entitlements on org switch so stale data from org A never leaks into org B.
 *
 * Concurrent callers share the store's single in-flight promise, so mounting N
 * components together issues one request. That does not hold on an org *switch*:
 * `invalidateEntitlements()` clears the in-flight promise, so each mounted
 * consumer starts its own fetch. Harmless — the store's token guard makes it
 * last-one-wins — but N requests, not one.
 */
export const useFeatureEntitlement = (featureId: string) => {
    const { organization } = useOrganizationManager();
    const subscriptionStore = useSubscriptionStore();

    watch(
        () => organization.value?.identity,
        (orgId, prevOrgId) => {
            if (prevOrgId !== undefined) {
                // Org switched — discard stale entitlements immediately.
                subscriptionStore.invalidateEntitlements();
            }
            if (orgId) {
                subscriptionStore.fetchEntitlements();
            }
        },
        { immediate: true }
    );

    const isEnabled = computed(() => subscriptionStore.isEntitled(featureId));
    const isLoading = computed(() => subscriptionStore.loading);
    /**
     * Why the feature is unavailable, or null when it is usable.
     *
     * `globally_disabled` is not a subscription problem — buying something
     * changes nothing and checkout refuses — so a caller that offers "subscribe"
     * needs to be able to tell the two apart.
     */
    const unavailableReason = computed(() =>
        subscriptionStore.unavailableReason(featureId)
    );

    return { isEnabled, isLoading, unavailableReason };
};
